import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Zap, Camera, ShieldCheck, Landmark, Calendar, FileSignature,
  Upload, Download, ArrowRight, Check, ChevronDown, Star, Bot,
  Package, Menu, X, Play, Sparkles, Boxes, Palette, Activity,
  Repeat, TrendingUp, Calculator, FolderKanban, BarChart3, Briefcase,
  Mail, Phone, Building2,
} from 'lucide-react';
import DemoRequestForm from '../components/landing/DemoRequestForm';
import HeroPreview from '../components/landing/HeroPreview';
import CountUp from '../components/landing/CountUp';
import RotatingText from '../components/landing/RotatingText';
import FloatingOrbs from '../components/landing/FloatingOrbs';
import MarqueeRow from '../components/landing/MarqueeRow';
import TiltCard from '../components/landing/TiltCard';
import ScrollProgressBar from '../components/landing/ScrollProgressBar';

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const features = [
  { icon: Bot, title: 'Facturación con IA', desc: 'Genera facturas, presupuestos y albaranes en segundos. Dicta por voz o escribe y el asistente crea el documento.', accent: 'from-orange-500 to-amber-400' },
  { icon: Camera, title: 'Gastos con OCR', desc: 'Escanea tickets, PDFs multi-página y fotos de iPhone (HEIC). La IA extrae todos los datos automáticamente.', accent: 'from-sky-500 to-cyan-400' },
  { icon: ShieldCheck, title: 'Verifactu AEAT', desc: 'Cumple con la nueva normativa: hash SHA-256 encadenado, QR obligatorio, validador NIF/CIF/NIE y trazabilidad completa.', accent: 'from-emerald-500 to-teal-400' },
  { icon: Landmark, title: 'Conciliación Bancaria', desc: 'Importa extractos CSV o conecta vía PSD2. Concilia automáticamente con facturas y gastos.', accent: 'from-violet-500 to-purple-400' },
  { icon: Boxes, title: 'Inventario Artículos', desc: 'Stock automático: cada factura descuenta el inventario y el panel te avisa antes de quedarte sin existencias.', accent: 'from-fuchsia-500 to-pink-400' },
  { icon: Palette, title: 'Plantillas PDF', desc: '6 diseños profesionales seleccionables a nivel global. Tus facturas siempre con la misma identidad visual.', accent: 'from-rose-500 to-red-400' },
  { icon: Repeat, title: 'Suscripciones + SEPA', desc: 'Cobros recurrentes con MRR/ARR, mandatos SEPA, IBAN validado y generación de lote pain.008.', accent: 'from-indigo-500 to-blue-400' },
  { icon: FolderKanban, title: 'Time Tracking', desc: 'Cronómetro flotante, proyectos rentables, facturación de horas en un clic y panel de margen real.', accent: 'from-teal-500 to-emerald-400' },
  { icon: Calculator, title: 'Predicción Fiscal', desc: 'Calcula 303, 130, 111, 115, 202, 200 e IRPF anual con simulador what-if antes de cada vencimiento.', accent: 'from-amber-500 to-yellow-400' },
  { icon: Sparkles, title: 'Asesor IA fiscal', desc: 'Chat con Claude entrenado en tu contabilidad: pregunta cuánto pagarás de IVA o cómo optimizar tu IRPF.', accent: 'from-violet-600 to-fuchsia-500' },
  { icon: FileSignature, title: 'Agente de Inspección', desc: 'Asistente IA para inspecciones de Hacienda: análisis automático y generación de informes PDF para el inspector.', accent: 'from-blue-500 to-indigo-400' },
  { icon: Package, title: 'Centro de Exportación', desc: 'Genera un ZIP completo para tu gestor con resumen 303, libros IVA, facturas, gastos y T1-T4 en 1 click.', accent: 'from-pink-500 to-rose-400' },
];

const modules = [
  { icon: BarChart3, title: 'Dashboard inteligente', desc: 'KPIs en tiempo real con alertas, próximos vencimientos y top clientes.' },
  { icon: Briefcase, title: 'Clientes y Proveedores', desc: 'CRM ligero con portal de cliente white-label y mensajería.' },
  { icon: TrendingUp, title: 'Cash Flow what-if', desc: 'Proyección de caja a 12 meses con escenarios optimista/pesimista.' },
  { icon: Calendar, title: 'Calendario fiscal', desc: 'Vencimientos AEAT, reuniones e inspecciones con alertas automáticas.' },
  { icon: Upload, title: 'Importación masiva', desc: 'Sube años de facturas Excel/PDF y el OCR las procesa en lote.' },
  { icon: Download, title: 'Exportaciones gestoría', desc: 'Libros AEAT, Modelos 303/130/111/115/347/349 y SII.' },
];

const faqs = [
  { q: '¿Qué es Verifactu y por qué lo necesito?', a: 'Verifactu es el sistema obligatorio de la AEAT que exige que todo software de facturación encadene las facturas con hash y código QR. Facturcal lo implementa de serie desde el primer día.' },
  { q: '¿Puedo migrar mis datos desde otro programa?', a: 'Sí. Tenemos importación masiva con OCR que procesa facturas antiguas en PDF, Excel o CSV. Nuestro equipo te ayuda en la migración durante el onboarding.' },
  { q: '¿Cómo funciona la solicitud de demo?', a: 'Rellena el formulario y un especialista te contactará en menos de 24 horas laborables para mostrarte el sistema con tus datos reales.' },
  { q: '¿La IA puede crear facturas por voz?', a: 'Sí. Dicta "Hazme una factura de 10 horas de consultoría para el cliente X" y el sistema la genera automáticamente.' },
  { q: '¿Es válido para autónomos y pymes?', a: 'Facturcal está diseñado para autónomos individuales, profesionales colegiados, pymes y multi-empresa con varios usuarios y roles.' },
  { q: '¿Mis datos están seguros?', a: 'JWT con tokens seguros, encriptación bcrypt, HTTPS extremo a extremo, copias de seguridad cifradas y servidores en la UE (RGPD).' },
];

const testimonials = [
  { name: 'María García', role: 'Autónoma, Diseño Web', text: 'Me ahorra 5 horas a la semana. Creo facturas dictando por voz mientras conduzco.', stars: 5, img: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=100&h=100&fit=crop&crop=face' },
  { name: 'Carlos Ruiz', role: 'CEO, Construcciones Ruiz', text: 'La conciliación bancaria automática y el OCR han transformado mi contabilidad.', stars: 5, img: 'https://images.pexels.com/photos/14589344/pexels-photo-14589344.jpeg?w=100&h=100&fit=crop&crop=face' },
  { name: 'Ana López', role: 'Abogada', text: 'Los contratos con IA son increíbles. Y Verifactu me da tranquilidad total con Hacienda.', stars: 5, img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop&crop=face' },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div variants={fadeUp} className="border-b border-stone-200/60" data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group" data-testid={`faq-toggle-${idx}`}>
        <span className="text-base font-semibold text-stone-800 group-hover:text-[#FF6B4A] transition-colors pr-4">{q}</span>
        <div className={`w-7 h-7 rounded-full bg-stone-100 group-hover:bg-orange-50 flex items-center justify-center transition-all flex-shrink-0 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-4 h-4 text-stone-500" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-out ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <p className="text-stone-500 leading-relaxed text-sm">{a}</p>
      </div>
    </motion.div>
  );
};

const scrollToDemo = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById('demo');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const LandingPage = () => {
  const [mobileNav, setMobileNav] = useState(false);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <ScrollProgressBar />
      {/* SEO Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "SoftwareApplication",
        "name": "Facturcal",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web, iOS, Android",
        "url": "https://facturcal.com",
        "description": "Software de facturación para autónomos y pymes con IA, OCR, Verifactu, conciliación bancaria, inventario, suscripciones SEPA, time tracking, predicción fiscal y asesor IA con Claude. Pide una demo personalizada.",
        "featureList": [
          "Verifactu AEAT", "OCR de gastos con IA", "Conciliación bancaria automática",
          "Inventario de artículos con stock", "Plantillas PDF profesionales",
          "Suscripciones recurrentes + mandatos SEPA", "Time tracking y rentabilidad",
          "Predicción fiscal 303/130/111/115/IRPF", "Asesor IA fiscal con Claude",
          "Contratos generados con IA", "Analítica web en vivo",
          "Centro de exportación unificado", "Detección de duplicados",
          "Validación NIF/CIF/NIE AEAT", "PDF multi-página OCR automático",
        ],
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "127" },
        "publisher": { "@type": "Organization", "name": "Facturcal", "url": "https://facturcal.com" }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question", "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Organization",
        "name": "Facturcal",
        "url": "https://facturcal.com",
        "logo": "https://facturcal.com/logo.png",
        "description": "Plataforma SaaS de facturación con IA para autónomos y pymes españolas.",
        "areaServed": { "@type": "Country", "name": "España" },
        "sameAs": []
      })}} />

      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-stone-100">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-xl flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Facturcal</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Funciones</a>
            <a href="#modules" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Módulos</a>
            <a href="#testimonials" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Opiniones</a>
            <a href="#faq" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">FAQ</a>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-stone-700 hover:text-[#FF6B4A] transition-colors px-4 py-2 rounded-full hover:bg-orange-50" data-testid="landing-login-link">
              Iniciar sesión
            </Link>
            <a href="#demo" onClick={scrollToDemo} className="bg-[#FF6B4A] text-white px-5 py-2.5 text-sm font-semibold rounded-full hover:bg-[#E05A3E] transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-200/50" data-testid="landing-cta-nav">
              Solicitar Demo
            </a>
          </div>
          <button className="md:hidden p-2 rounded-xl hover:bg-stone-100" onClick={() => setMobileNav(!mobileNav)} data-testid="landing-mobile-menu">
            {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
        {mobileNav && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white/95 backdrop-blur-xl border-t border-stone-100 px-6 pb-6 pt-2 space-y-1">
            <a href="#features" onClick={() => setMobileNav(false)} className="block py-3 text-stone-600 font-medium rounded-xl px-3 hover:bg-stone-50">Funciones</a>
            <a href="#modules" onClick={() => setMobileNav(false)} className="block py-3 text-stone-600 font-medium rounded-xl px-3 hover:bg-stone-50">Módulos</a>
            <a href="#faq" onClick={() => setMobileNav(false)} className="block py-3 text-stone-600 font-medium rounded-xl px-3 hover:bg-stone-50">FAQ</a>
            <div className="pt-3 space-y-2">
              <Link to="/login" onClick={() => setMobileNav(false)} className="block text-center py-3 rounded-full border border-stone-200 text-stone-700 font-semibold">Iniciar sesión</Link>
              <a href="#demo" onClick={(e) => { scrollToDemo(e); setMobileNav(false); }} className="block bg-[#FF6B4A] text-white text-center py-3 rounded-full font-semibold shadow-lg shadow-orange-200/50">Solicitar Demo</a>
            </div>
          </motion.div>
        )}
      </header>

      <main>
        {/* HERO */}
        <section className="pt-28 pb-20 md:pt-36 md:pb-28 relative overflow-hidden">
          <FloatingOrbs count={6} />
          <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center lg:text-left">
                <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-orange-50 text-[#FF6B4A] px-4 py-1.5 rounded-full text-xs font-semibold mb-6 border border-orange-100 backdrop-blur-sm">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </motion.span>
                  Software de Facturación #1 con IA · Verifactu 2026
                </motion.div>
                <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-stone-950 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Factura, automatiza{' '}
                  <br className="hidden sm:block" />
                  y crece con{' '}
                  <RotatingText
                    words={['IA', 'Verifactu', 'OCR', 'Claude']}
                    interval={2200}
                  />
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg text-stone-500 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-10">
                  La plataforma completa para autónomos y pymes que cumple con <strong className="text-stone-700">Verifactu</strong>, concilia bancos, escanea gastos por OCR, gestiona inventario, suscripciones SEPA y predice tus impuestos con inteligencia artificial.
                </motion.p>
                <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <motion.a
                    href="#demo"
                    onClick={scrollToDemo}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center justify-center gap-2 bg-[#FF6B4A] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-xl shadow-orange-200/40 relative overflow-hidden group"
                    data-testid="hero-cta"
                  >
                    <span className="relative z-10">Solicitar Demo</span>
                    <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
                    <span className="absolute inset-0 bg-gradient-to-r from-[#FF6B4A] via-[#FBBF24] to-[#FF6B4A] bg-[length:200%_auto]" style={{ animation: 'gradient-shift 3s ease infinite' }} />
                  </motion.a>
                  <motion.a
                    href="#features"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center justify-center gap-2 bg-white/80 backdrop-blur text-stone-700 px-8 py-3.5 rounded-full text-base font-semibold border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-all"
                    data-testid="hero-secondary"
                  >
                    <Play className="w-4 h-4" /> Ver funciones
                  </motion.a>
                </motion.div>
                <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-stone-400">
                  <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Demo personalizada</span>
                  <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Verifactu AEAT</span>
                  <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />IA integrada</span>
                  <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Migración guiada</span>
                </motion.div>
              </motion.div>

              <div className="lg:pl-8">
                <HeroPreview />
              </div>
            </div>
          </div>
        </section>

        {/* MARQUEE — trust signals (estándares fiscales, no marcas comerciales) */}
        <section className="py-10 border-y border-stone-100 bg-stone-50/30 relative">
          <p className="text-center text-xs uppercase tracking-widest text-stone-400 font-semibold mb-6">
            Cumplimiento y estándares en una sola plataforma
          </p>
          <MarqueeRow speed={32}>
            {[
              'Verifactu', 'AEAT', 'SEPA', 'PSD2', 'OCR · IA', 'Modelo 303',
              'Modelo 130', 'IRPF 111/115', 'Modelo 200', 'Modelo 347', 'XLSX · PDF · ZIP',
              'Multi-empresa', 'Asesor IA fiscal', 'Predicción fiscal', 'Conciliación bancaria',
              'Time tracking', 'Suscripciones recurrentes',
            ].map((label) => (
              <div
                key={label}
                className="flex items-center gap-2 px-5 py-2 bg-white border border-stone-200 rounded-full whitespace-nowrap shadow-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B4A]" />
                <span className="text-sm font-semibold text-stone-700">{label}</span>
              </div>
            ))}
          </MarqueeRow>
        </section>

        {/* STATS — números animados al scroll */}
        <section className="py-14 bg-gradient-to-r from-stone-900 via-stone-950 to-stone-900 relative overflow-hidden">
          <FloatingOrbs count={3} palette={['#FF6B4A', '#FBBF24', '#A78BFA']} blur={120} />
          <div className="absolute inset-0 opacity-[0.04] bg-grid-pattern" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center relative z-10">
            {[
              { val: 500, suffix: '+', label: 'Empresas activas' },
              { val: 98, suffix: '%', label: 'Satisfacción' },
              { val: 30, suffix: 's', prefix: '< ', label: 'Crear factura' },
              { val: 100, suffix: '%', label: 'Verifactu' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <p className="text-3xl md:text-5xl font-extrabold bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] bg-clip-text text-transparent" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <CountUp end={s.val} suffix={s.suffix} prefix={s.prefix || ''} duration={1800} />
                </p>
                <p className="text-xs text-stone-500 mt-2 uppercase tracking-widest">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="mb-16 text-center max-w-2xl mx-auto">
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3">Funcionalidades estrella</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                12 superpoderes para tu negocio
              </motion.h2>
              <motion.p variants={fadeUp} className="text-stone-500">Facturación, gastos, banca, inventario, suscripciones, time tracking, impuestos, contratos y mucho más en una sola plataforma.</motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  className="bg-white rounded-2xl border border-stone-100 p-6 hover:shadow-2xl hover:shadow-orange-100/50 transition-all duration-300 group relative overflow-hidden"
                  data-testid={`feature-${i}`}
                >
                  {/* Hover glow effect */}
                  <div className={`absolute -inset-px rounded-2xl bg-gradient-to-br ${f.accent} opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none`} />
                  <motion.div
                    className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${f.accent} flex items-center justify-center mb-4 shadow-md`}
                    whileHover={{ scale: 1.15, rotate: -6 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <f.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </motion.div>
                  <h3 className="relative text-base font-bold text-stone-900 mb-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.title}</h3>
                  <p className="relative text-sm text-stone-400 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* MODULES */}
        <section id="modules" className="py-24 md:py-32 bg-gradient-to-b from-stone-50/50 to-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center max-w-2xl mx-auto mb-16">
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3">Más módulos incluidos</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Todo lo que tu gestoría te pedirá
              </motion.h2>
              <motion.p variants={fadeUp} className="text-stone-500">Cumplimiento fiscal completo y herramientas avanzadas sin coste adicional.</motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {modules.map((m, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-orange-200 hover:shadow-md transition-all flex gap-4" data-testid={`module-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <m.icon className="w-5 h-5 text-[#FF6B4A]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-stone-900 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{m.title}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed">{m.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* PLANS — sin precios, todo via demo personalizada */}
        <section id="plans" className="py-24 md:py-32 bg-stone-50/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3">Planes</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                3 planes adaptados a tu volumen
              </motion.h2>
              <motion.p variants={fadeUp} className="text-stone-500 mt-3 max-w-xl mx-auto">
                Cada negocio es único. Pide una <strong className="text-stone-700">demo personalizada</strong> y te ajustamos el plan a tu volumen real.
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {/* Starter */}
              <motion.div variants={fadeUp}>
                <TiltCard className="bg-white rounded-3xl border border-stone-200 p-7 hover:shadow-2xl hover:shadow-stone-200/50 transition-all">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-1">Autónomo</p>
                <h3 className="text-xl font-extrabold text-stone-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Starter</h3>
                <p className="text-sm text-stone-500 mb-6">Para autónomos individuales y profesionales independientes.</p>
                <ul className="space-y-2.5 mb-8">
                  {['Verifactu + QR', 'Dashboard + KPIs', 'Gastos con OCR', 'Asistente IA básico', 'PDF personalizado'].map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-stone-600"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <a href="#demo" onClick={scrollToDemo} className="block text-center py-3 rounded-full border border-stone-200 text-stone-700 font-semibold hover:bg-stone-50 transition-all" data-testid="plan-starter-cta">Solicitar Demo</a>
                </TiltCard>
              </motion.div>

              {/* Pro — destacado */}
              <motion.div variants={fadeUp} className="md:scale-[1.04] pt-3 relative">
                <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white text-[10px] font-bold px-4 py-1 rounded-full tracking-wider uppercase shadow-lg whitespace-nowrap">Más popular</div>
                <TiltCard className="bg-gradient-to-b from-stone-900 to-stone-950 rounded-3xl p-7 text-white relative shadow-2xl shadow-stone-900/40 ring-1 ring-white/10 overflow-hidden">
                <FloatingOrbs count={2} palette={['#FF6B4A', '#FBBF24']} blur={60} />
                <div className="relative z-[1]">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 ring-1 ring-orange-300/30 flex items-center justify-center mb-4 mt-2">
                  <Sparkles className="w-5 h-5 text-orange-300" />
                </div>
                <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-1">Pymes</p>
                <h3 className="text-xl font-extrabold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Pro</h3>
                <p className="text-sm text-stone-400 mb-6">Para pymes y profesionales con volumen creciente.</p>
                <ul className="space-y-2.5 mb-8">
                  {['Todo en Starter', 'Inventario + Plantillas', 'Conciliación PSD2', 'Suscripciones + SEPA', 'Time tracking + Rentabilidad', 'Predicción fiscal + Asesor IA', 'Contratos con IA', 'Soporte prioritario'].map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-stone-300"><Check className="w-4 h-4 text-[#FF6B4A] mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <a href="#demo" onClick={scrollToDemo} className="block text-center py-3 rounded-full bg-gradient-to-r from-[#FF6B4A] to-[#E05A3E] text-white font-semibold hover:opacity-90 transition-all shadow-lg shadow-orange-500/30" data-testid="plan-pro-cta">Solicitar Demo</a>
                </div>
                </TiltCard>
              </motion.div>

              {/* Enterprise */}
              <motion.div variants={fadeUp}>
                <TiltCard className="bg-white rounded-3xl border border-stone-200 p-7 hover:shadow-2xl hover:shadow-stone-200/50 transition-all">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-4">
                  <Building2 className="w-5 h-5 text-violet-600" />
                </div>
                <p className="text-xs font-semibold tracking-widest uppercase text-stone-400 mb-1">Empresas</p>
                <h3 className="text-xl font-extrabold text-stone-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Enterprise</h3>
                <p className="text-sm text-stone-500 mb-6">Multi-empresa, multi-usuario, gestorías y partners.</p>
                <ul className="space-y-2.5 mb-8">
                  {['Todo en Pro', 'Multi-empresa (RBAC)', 'Panel administrador', 'Analítica web live', 'Importación masiva guiada', 'API personalizada', 'Soporte dedicado'].map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-stone-600"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <a href="#demo" onClick={scrollToDemo} className="block text-center py-3 rounded-full border border-stone-200 text-stone-700 font-semibold hover:bg-stone-50 transition-all" data-testid="plan-enterprise-cta">Solicitar Demo</a>
                </TiltCard>
              </motion.div>
            </motion.div>

            <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center text-sm text-stone-400 mt-10">
              No publicamos precios porque te ajustamos un plan personalizado en función de tu volumen, equipo e integraciones. <strong className="text-stone-700">Solicita demo y te lo enseñamos.</strong>
            </motion.p>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3">Opiniones</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Lo que dicen nuestros usuarios</motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl border border-stone-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-0.5 mb-4">{Array(t.stars).fill(0).map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-stone-600 leading-relaxed mb-6 text-sm">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    <div>
                      <p className="text-sm font-semibold text-stone-800">{t.name}</p>
                      <p className="text-xs text-stone-400">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-24 md:py-32 bg-stone-50/50">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3 text-center">FAQ</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950 text-center mb-12" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Preguntas frecuentes</motion.h2>
              {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
            </motion.div>
          </div>
        </section>

        {/* DEMO FORM */}
        <section id="demo" className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-stone-50 via-white to-orange-50/30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 rounded-full blur-[120px] opacity-30 -translate-y-1/4 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-100 rounded-full blur-[100px] opacity-40 translate-y-1/4 -translate-x-1/4" />
          <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left content */}
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-[#FF6B4A] mb-3">Solicita tu demo</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-950 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Ve Facturcal en acción con tus datos
                </h2>
                <p className="text-stone-500 leading-relaxed mb-8">
                  Cuéntanos brevemente sobre tu negocio. Un especialista te contactará en menos de 24 horas laborables con una demo personalizada de 30 minutos.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Demo en directo adaptada a tu sector',
                    'Migración gratuita desde tu sistema actual',
                    'Asesoramiento Verifactu sin compromiso',
                    'Plan de precios ajustado a tu volumen',
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-stone-700">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      {t}
                    </li>
                  ))}
                </ul>
                <div className="space-y-3 pt-6 border-t border-stone-200">
                  <a href="mailto:info@facturcal.com" className="flex items-center gap-3 text-sm text-stone-600 hover:text-[#FF6B4A] transition-colors">
                    <Mail className="w-4 h-4" /> info@facturcal.com
                  </a>
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <Phone className="w-4 h-4" /> Respuesta en 24h laborables
                  </div>
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <Building2 className="w-4 h-4" /> Para autónomos, pymes y gestorías
                  </div>
                </div>
              </div>

              {/* Right form */}
              <DemoRequestForm source="landing-main" />
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 md:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF6B4A] rounded-full blur-[200px] opacity-10" />
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              ¿Hablamos sobre tu facturación?
            </h2>
            <p className="text-stone-400 mb-10 max-w-xl mx-auto">Únete a cientos de autónomos y empresas que ya gestionan su negocio con inteligencia artificial.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#demo" onClick={scrollToDemo} className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF6B4A] to-[#E05A3E] text-white px-8 py-3.5 rounded-full text-base font-semibold hover:opacity-90 transition-all shadow-xl shadow-orange-500/20" data-testid="final-cta">
                Solicitar Demo <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/login" className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-white px-8 py-3.5 rounded-full text-base font-medium border border-white/10 hover:border-white/20 transition-all" data-testid="final-login">
                Ya tengo cuenta
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-stone-950 border-t border-stone-800/50 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-lg flex items-center justify-center">
                  <FileText className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
                <span className="text-lg font-extrabold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Facturcal</span>
              </div>
              <p className="text-sm text-stone-500 leading-relaxed">Software de facturación con IA para autónomos y pymes adaptado a Verifactu.</p>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-3">Producto</p>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-stone-500 hover:text-white transition-colors">Funciones</a></li>
                <li><a href="#modules" className="text-stone-500 hover:text-white transition-colors">Módulos</a></li>
                <li><a href="#plans" className="text-stone-500 hover:text-white transition-colors">Planes</a></li>
                <li><a href="#demo" onClick={scrollToDemo} className="text-stone-500 hover:text-white transition-colors">Solicitar Demo</a></li>
                <li><Link to="/login" className="text-stone-500 hover:text-white transition-colors">Iniciar sesión</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-3">Legal</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacidad" className="text-stone-500 hover:text-white transition-colors">Aviso Legal</Link></li>
                <li><Link to="/privacidad" className="text-stone-500 hover:text-white transition-colors">Privacidad</Link></li>
                <li><Link to="/privacidad" className="text-stone-500 hover:text-white transition-colors">Cookies</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-stone-500 mb-3">Soporte</p>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@facturcal.com" className="text-stone-500 hover:text-white transition-colors">info@facturcal.com</a></li>
                <li><a href="#faq" className="text-stone-500 hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800/50 pt-8 text-center text-xs text-stone-600">
            © {new Date().getFullYear()} Facturcal. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
